import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Typography
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { User, UserCreate, UserUpdate } from '../services/api';

interface UserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (user: UserCreate | UserUpdate) => Promise<void>;
}

const ROLES = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'BODEGA', label: 'Bodega' },
  { value: 'CUENTAS_POR_COBRAR', label: 'Cuentas por Cobrar' },
  { value: 'VENTAS', label: 'Ventas' },
  { value: 'SERVICIO_TECNICO_ADMIN', label: 'Admin Servicio Técnico' },
  { value: 'TECNICO', label: 'Técnico' },
  { value: 'LECTOR', label: 'Lector de Contadores' },
  { value: 'IDG', label: 'IDG' },
];

const UserDialog: React.FC<UserDialogProps> = ({ open, user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    roles: ['ADMIN'] as string[],
    password: '',
    is_active: true,
    customer_id: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        full_name: user.full_name,
        roles: user.roles || [],
        password: '',
        is_active: user.is_active,
        customer_id: user.customer_id || ''
      });
    } else {
      setFormData({
        email: '',
        full_name: '',
        roles: ['ADMIN'],
        password: '',
        is_active: true,
        customer_id: ''
      });
    }
    setError('');
  }, [user, open]);

  // Toggle a role in the roles array
  const toggleRole = (roleValue: string) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleValue)
        ? prev.roles.filter(r => r !== roleValue)
        : [...prev.roles, roleValue]
    }));
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      // Validate at least one role is selected
      if (formData.roles.length === 0) {
        setError('Debe seleccionar al menos un rol');
        setLoading(false);
        return;
      }

      if (user) {
        // Update - don't send password if empty
        const updateData: UserUpdate = {
          email: formData.email,
          full_name: formData.full_name,
          roles: formData.roles,
          is_active: formData.is_active,
          customer_id: formData.customer_id || undefined
        };
        await onSave(updateData);
      } else {
        // Create - password is required
        if (!formData.password) {
          setError('La contraseña es requerida para nuevos usuarios');
          setLoading(false);
          return;
        }
        const createData: UserCreate = {
          email: formData.email,
          full_name: formData.full_name,
          roles: formData.roles,
          password: formData.password,
          customer_id: formData.customer_id || undefined
        };
        await onSave(createData);
      }
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Handle Pydantic validation errors
        setError(detail.map((e: any) => e.msg).join(', '));
      } else {
        setError(detail || 'Error al guardar usuario');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {user ? 'Editar Usuario' : 'Crear Usuario'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Nombre Completo"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            label="Correo Electrónico"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />

          {/* Multi-role selection with checkboxes */}
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">
              <Typography variant="subtitle2" color="textSecondary">
                Roles (seleccione uno o más)
              </Typography>
            </FormLabel>
            <FormGroup>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0 }}>
                {ROLES.map((role) => (
                  <FormControlLabel
                    key={role.value}
                    control={
                      <Checkbox
                        checked={formData.roles.includes(role.value)}
                        onChange={() => toggleRole(role.value)}
                        disabled={loading}
                        size="small"
                      />
                    }
                    label={role.label}
                  />
                ))}
              </Box>
            </FormGroup>
          </FormControl>

          {!user && (
            <TextField
              fullWidth
              label="Contraseña"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!user}
              helperText={user ? 'Dejar en blanco para no cambiar' : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <TextField
            fullWidth
            label="ID de Cliente (opcional)"
            value={formData.customer_id}
            onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
          />
          {user && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Usuario Activo"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {user ? 'Guardar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;
